import React, { useMemo, useState } from 'react';
import { Box, Button, Stack, ButtonProps, IconButton, Grid } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';

type ButtonOption<T extends string | boolean> = {
    value: T;
    label: React.ReactNode;
    details?: React.ReactNode;
    disabled?: boolean;
};

interface ButtonRadioGroupProps<T extends string | boolean> {
    options: ButtonOption<T>[];
    value?: T;
    defaultValue?: T;
    onChange?: (value: T, option: ButtonOption<T>) => void;
    fullWidth?: boolean;
    size?: 'small' | 'medium' | 'large';
    variant?: ButtonProps['variant'];
    selectedVariant?: ButtonProps['variant'];
    color?: ButtonProps['color'];
    ariaLabel?: string;
    onInfoClick?: () => void;
    optionClassName?: string;
    detailClassName?: string;
    emptyDefaultValue?: boolean;
}

export const ButtonRadioGroup = <T extends string | boolean>({
    options,
    value,
    defaultValue,
    onChange,
    fullWidth = false,
    size = 'medium',
    variant = 'outlined',
    selectedVariant = 'contained',
    color = 'primary',
    ariaLabel,
    onInfoClick,
    optionClassName,
    detailClassName,
    emptyDefaultValue = false,
}: ButtonRadioGroupProps<T>) => {
    const isControlled = value !== undefined;
    const [ internal, setInternal ] = useState<T | undefined>(
        emptyDefaultValue ? undefined : defaultValue ?? options[0]?.value ?? '',
    );
    const selected = isControlled ? value as T : internal;

    const handleSelect = (opt: ButtonOption<T>) => {
        if (!isControlled) {
            setInternal(opt.value);
        }
        onChange?.(opt.value, opt);
    };
    const hasDetails = useMemo(() => {
        return options.some(option => option.details);
    }, [ options ]);

    return (
        <Box role="radiogroup" aria-label={ariaLabel}>
            <Box sx={{
                display: 'flex',
                alignItems: 'flex-start',
                padding: '0 1px',
                width: fullWidth ? '100%' : 'fit-content',
            }}>
                <Stack
                    direction="row"
                    sx={{
                        flex: fullWidth ? 1 : undefined,
                        borderColor: 'divider',
                        overflow: 'hidden',
                    }}
                >
                    {options.map((opt) => {
                        const isSelected = selected === opt.value;
                        return (
                            <Button
                                key={opt.value + ''}
                                variant={isSelected ? selectedVariant : variant}
                                color={isSelected ? color : undefined}
                                size={size}
                                disabled={opt.disabled}
                                role="radio"
                                aria-checked={isSelected}
                                onClick={() => handleSelect(opt)}
                                sx={{
                                    flex: fullWidth ? 1 : undefined,
                                    textTransform: 'none',
                                    fontWeight: isSelected ? 600 : 500,
                                    borderRadius: 0,
                                    '&:first-of-type': { borderRadius: '5px 0 0 5px' },
                                    '&:last-of-type': { borderRadius: '0 5px 5px 0' },
                                }}
                                className={optionClassName}
                            >
                                {opt.label}
                            </Button>
                        );
                    })}
                </Stack>
                {onInfoClick && (
                    <IconButton size="small" sx={{ ml: 1, mt: 0.5 }} onClick={onInfoClick}>
                        <InfoIcon fontSize="small"/>
                    </IconButton>
                )}
            </Box>

            {hasDetails && (
                <Grid
                    sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        mt: 1,
                        mb: 1,
                        width: fullWidth ? '100%' : 'fit-content',
                    }}
                >
                    {options.map((opt, i) => (
                        <Grid key={`${opt.value}-details`} sx={{ flex: 1 }}>
                            {opt.details && (
                                <Box
                                    sx={{
                                        height: '100%',
                                        borderStyle: 'solid',
                                        borderWidth: 1,
                                        borderColor: 'divider',
                                        ...(
                                            i > 0 && { borderLeftWidth: 0 }
                                        ),
                                        borderTopLeftRadius: i === 0 ? 5 : 0,
                                        borderBottomLeftRadius: i === 0 ? 5 : 0,
                                        borderTopRightRadius: i === options.length - 1 ? 5 : 0,
                                        borderBottomRightRadius: i === options.length - 1 ? 5 : 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                    className={detailClassName}
                                >
                                    {opt.details}
                                </Box>
                            )}
                        </Grid>
                    ))}
                </Grid>
            )}
        </Box>
    );
};
